import { flow, computed, makeObservable, observable, reaction } from 'mobx';
import { GlobalStore } from '@roc/feature-app-core';
import { DocumentStore } from '@roc/feature-documents';
import { LoanStore } from '@roc/feature-loans';
import { FormStore } from '@roc/feature-app-core';
import { ExpectedClosingDateService } from '../../services/documentForms/expectedClosingDateService';
import { shouldBeAbleToSubmitSpecificForm } from '../../utils/documentForms';
import { DocumentName } from '../../constants';
import { formatDate } from '@roc/feature-utils';
import { OTHER_ADJOURNMENT_REASON } from '../../components/documentForms/expectedClosingDate/constants';

export const expectedClosingDateForm = {
  fields: {
    date: {
      value: null,
      error: null,
      rule: '',
    },
    reasonForAdjournment: {
      value: null,
      error: null,
      rule: '',
    },
    closingDead: {
      value: false,
      error: null,
      rule: '',
    },
    closingDeadReason: {
      value: '',
      error: null,
      rule: [{ required_if: ['closingDead', true] }],
    },
    otherAdjournmentReason: {
      value: '',
      error: null,
      rule: [{ required_if: ['reasonForAdjournment', OTHER_ADJOURNMENT_REASON] }],
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class ExpectedClosingDateStore extends FormStore {
  private documentStore: DocumentStore;
  private expectedClosingDateService: ExpectedClosingDateService;

  globalStore: GlobalStore;
  loanStore: LoanStore;

  adjournmentData: any = null;

  afterSubmit: (() => void | Promise<void>) | null = null;

  showReasonForAdjournment = false;
  showClosingDead = false;

  private adjournmentDataLoaded = false;

  private dateChangeDisposer: (() => void) | null = null;

  private initialDateMs: number | null = null;

  constructor(
    documentStore: DocumentStore,
    loanStore: LoanStore,
    globalStore: GlobalStore
  ) {
    super({ ...expectedClosingDateForm }, globalStore);

    this.documentStore = documentStore;
    this.loanStore = loanStore;
    this.globalStore = globalStore;
    this.expectedClosingDateService = new ExpectedClosingDateService();

    makeObservable(this, {
      initialize: flow,
      setupDateWatcher: flow,
      submitexpectedClosingDateState: flow,
      getLoanClosingAdjournmentData: flow,

      adjournmentData: observable,
      showReasonForAdjournment: observable,
      showClosingDead: observable,
      areRequiredAdjournmentFieldsFilled: computed,
    });
  }

  private hasExistingAdjournmentData(): boolean {
    const reason = this.form?.fields?.reasonForAdjournment?.value;

    if (typeof reason === 'string') return reason.trim().length > 0;
    return !!reason;
  }

  private hasExistingClosingDeadData(): boolean {
    const closingDead = this.form?.fields?.closingDead?.value;
    const closingDeadReason = this.form?.fields?.closingDeadReason?.value;

    const hasClosingDeadFlag = closingDead === true;

    const hasReason =
      typeof closingDeadReason === 'string'
        ? closingDeadReason.trim().length > 0
        : !!closingDeadReason;

    return hasClosingDeadFlag || hasReason;
  }

  *initialize() {
    this.reset();

    this.loadForm({
      date: this.loanStore.loanDetails.expectedClosingDate ?? null,
      reasonForAdjournment: this.adjournmentData?.reasonForAdjournment ?? null,
      closingDead: this.adjournmentData?.closingDead ?? false,
      closingDeadReason: this.adjournmentData?.closingDeadReason ?? '',
      otherAdjournmentReason:
        this.adjournmentData?.otherAdjournmentReason ?? '',
    });

    const initial = this.form?.fields?.date?.value;
    if (initial instanceof Date) {
      this.initialDateMs = initial.getTime();
    } else if (typeof initial === 'string' && initial.trim() !== '') {
      const parsed = new Date(initial);
      this.initialDateMs = isNaN(parsed.getTime()) ? null : parsed.getTime();
    } else {
      this.initialDateMs = null;
    }

    this.showReasonForAdjournment = this.hasExistingAdjournmentData();
    this.showClosingDead = this.hasExistingClosingDeadData();

    yield this.setupDateWatcher();
  }

  *setupDateWatcher() {
    this.disposeDateWatcher();

    this.dateChangeDisposer = reaction(
      () => {
        const d = this.form?.fields?.date?.value;
        const reasonForAdjournment = this.form?.fields?.reasonForAdjournment?.value;
        const closingDead = this.form?.fields?.closingDead?.value;

        let dateMs: number | null = null;
        if (d instanceof Date) {
          dateMs = d.getTime();
        } else if (typeof d === 'string') {
          const trimmed = d.trim();
          if (trimmed === '') {
            dateMs = null;
          } else {
            const parsed = new Date(trimmed);
            dateMs = isNaN(parsed.getTime()) ? null : parsed.getTime();
          }
        } else {
          dateMs = (d as any) ?? null;
        }

        return {
          dateMs,
          reasonForAdjournment,
          closingDead,
        };
      },
      (snapshot) => {
        const hasDate = snapshot.dateMs != null;
        const wasInitiallySet = this.initialDateMs != null;

        const dateChanged =
          hasDate && wasInitiallySet && snapshot.dateMs !== this.initialDateMs;

        const dateCleared = !hasDate && wasInitiallySet;

        const showAdjournment = dateChanged || this.hasExistingAdjournmentData();
        const showClosingDead =
          !hasDate && (dateCleared || this.hasExistingClosingDeadData());

        this.showReasonForAdjournment = showAdjournment;
        this.showClosingDead = showClosingDead;

        if (snapshot.reasonForAdjournment !== OTHER_ADJOURNMENT_REASON) {
          this.onFieldChange('otherAdjournmentReason', '');
        }
      },
      { fireImmediately: true }
    );
  }

  disposeDateWatcher() {
    if (this.dateChangeDisposer) {
      this.dateChangeDisposer();
      this.dateChangeDisposer = null;
    }
  }

  get canEdit() {
    const entitlements = this.globalStore.userFeatures.closingEntitlements;

    return shouldBeAbleToSubmitSpecificForm(
      entitlements?.isSubmissionAllowed,
      entitlements?.isSubmissionNotAllowedOnly,
      entitlements?.isSubmissionAllowedOnly,
      DocumentName.EXPECTED_CLOSING_DATE
    );
  }

  get areRequiredAdjournmentFieldsFilled(): boolean {
    if (!this.showReasonForAdjournment) return true;
    const reason = this.form?.fields?.reasonForAdjournment?.value;
    if (!reason) return false;
    if (reason === OTHER_ADJOURNMENT_REASON) {
      const otherReason = this.form?.fields?.otherAdjournmentReason?.value;
      return typeof otherReason === 'string' && otherReason.trim().length > 0;
    }
    return true;
  }

  public shouldBeAbleToSubmitExpectedClosingDate(): boolean {
    return this.canEdit;
  }

  public canSubmitExpectedClosingDate(): boolean {
    if (this.showClosingDead && this.form?.fields?.closingDead?.value === false) {
      return false;
    }
    return this.canEdit && this.areRequiredAdjournmentFieldsFilled;
  }

  *submitexpectedClosingDateState() {
    if (!this.runFormValidationWithMessage()) {
      return;
    }
    try {
      const values = this.getFormValues();

      yield this.expectedClosingDateService.submitExpectedClosingDate(
        this.loanStore.loanDetails.loanId,
        values.date ? formatDate(values.date, 'yyyy-MM-dd') : null
      );

      if (this.adjournmentDataLoaded) {
        yield this.expectedClosingDateService.saveLoanClosingAdjournmentData({
          loanId: this.loanStore.loanDetails.loanId,
          reasonForAdjournment: values.reasonForAdjournment,
          closingDead: values.closingDead,
          closingDeadReason: values.closingDeadReason,
          otherAdjournmentReason: values.otherAdjournmentReason,
        });
      }

      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Request made successful.',
      });

      this.documentStore.documentFormStore.closeCurrentForm();

      if (this.afterSubmit) {
        const cb = this.afterSubmit;
        this.afterSubmit = null;
        yield cb();
      }

      yield this.loanStore.refreshLoanDetails();
    } catch (e) {
      console.error(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while submitting the form.',
      });
    }
  }

  *getLoanClosingAdjournmentData() {
    try {
      const response =
        yield this.expectedClosingDateService.getLoanClosingAdjournmentData(
          this.loanStore.loanDetails.loanId
        );
      this.adjournmentData = response?.data?.data;
      this.adjournmentDataLoaded = true;
    } catch (e) {
      this.adjournmentDataLoaded = false;
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while fetching closing adjournment data.',
      });
    }
  }

  closeModal() {
    this.disposeDateWatcher();
    return this.documentStore.documentFormStore.setCurrentForm(null);
  }
}

export default ExpectedClosingDateStore;
